// Communication settings API fetchers.
//
// Server module: settings/communication. Mounted at
// /api/v1/settings/communication. All routes are tenant-scoped + staff-auth;
// writes additionally require admin role.

import { apiClient } from './client';
import { endpoints } from './endpoints';
import type {
  EmailPurpose,
  EmailSettings,
  ImportParsersResponse,
  PreferencesResponse,
  SmsSettings,
  TestEmailInput,
  TestResult,
  TestSmsInput,
  UpsertImapEmailInput,
  UpsertPreferencesInput,
  UpsertSmsInput,
  UpsertSmtpEmailInput,
} from '../types/api';

export async function getEmailSettings(
  purpose: EmailPurpose,
): Promise<EmailSettings> {
  const res = await apiClient.get<EmailSettings>(
    endpoints.communication.EMAIL(purpose),
  );
  return res.data;
}

export async function updateEmailSettings(
  purpose: EmailPurpose,
  body: UpsertSmtpEmailInput | UpsertImapEmailInput,
): Promise<EmailSettings> {
  const res = await apiClient.put<EmailSettings, typeof body>(
    endpoints.communication.EMAIL(purpose),
    body,
  );
  return res.data;
}

export async function testEmailConnection(
  purpose: EmailPurpose,
  body: TestEmailInput,
): Promise<TestResult> {
  const res = await apiClient.post<TestResult, TestEmailInput>(
    endpoints.communication.EMAIL_TEST(purpose),
    body,
  );
  return { success: res.data.success, message: res.data.message };
}

export async function getSmsSettings(): Promise<SmsSettings | null> {
  const res = await apiClient.get<SmsSettings | null>(
    endpoints.communication.SMS,
  );
  return res.data;
}

export async function updateSmsSettings(
  body: UpsertSmsInput,
): Promise<SmsSettings> {
  const res = await apiClient.put<SmsSettings, UpsertSmsInput>(
    endpoints.communication.SMS,
    body,
  );
  return res.data;
}

export async function testSmsConnection(
  body: TestSmsInput,
): Promise<TestResult> {
  const res = await apiClient.post<TestResult, TestSmsInput>(
    endpoints.communication.SMS_TEST,
    body,
  );
  return { success: res.data.success, message: res.data.message };
}

export async function getNotificationPreferences(): Promise<PreferencesResponse> {
  const res = await apiClient.get<PreferencesResponse>(
    endpoints.communication.PREFERENCES,
  );
  return res.data;
}

export async function updateNotificationPreferences(
  body: UpsertPreferencesInput,
): Promise<PreferencesResponse> {
  const res = await apiClient.put<PreferencesResponse, UpsertPreferencesInput>(
    endpoints.communication.PREFERENCES,
    body,
  );
  return res.data;
}

export async function getImportParsers(): Promise<ImportParsersResponse> {
  const res = await apiClient.get<ImportParsersResponse>(
    endpoints.communication.IMPORT_PARSERS,
  );
  return res.data;
}
